/**
 * Defence-in-depth filter against Claude Code's built-in slash-command output
 * (`/context`, `/cost`, `/compact`) leaking into Percy chat as an assistant text
 * message.
 *
 * Percy's backend already strips slash commands at the user-message boundary
 * (`agent.py::_is_slash_command` → `_refuse_slash_command`), but if a non-handler
 * code path (resume, autopilot replay, …) ever forwards `/context` to the Claude
 * CLI subprocess, the CLI prints an Anthropic-branded "Context Usage" report
 * that surfaces the underlying model + token budget + raw `mcp__…` tool names.
 * That breaks Percy's brand and exposes prompt internals to end users — see
 * the comment around `_SLASH_COMMAND_PATTERN` in agent.py.
 *
 * We detect that output at the renderer boundary and drop the message. If you
 * see the console.warn fire, root-cause the backend leak instead of widening
 * this filter.
 */

const HEADER_PATTERNS = [
  // /context: starts with "Context Usage" (optionally as a markdown heading).
  /^#{0,3}\s*Context Usage\b/im,
  // /cost: starts with "Total cost" or "Cost" header.
  /^#{0,3}\s*Total cost\b/im,
  // /compact: progress line the CLI prints while compacting.
  /^#{0,3}\s*Compact(ing)?\b/im,
];

// Signature lines the CLI emits inside its `/context` and `/cost` reports.
const MODEL_LINE = /(^|\n)\s*\*?\*?Model:\*?\*?\s*claude-/i;
const TOKENS_LINE = /(^|\n)\s*\*?\*?Tokens:\*?\*?\s*[\d.]+\s*[km]?\b/i;

export const isClaudeSlashCommandLeak = (content: string): boolean => {
  if (!content) return false;
  // Only look at the leading slice — the leak always shows up at the start of a
  // text block, and scanning the whole message would risk false positives on
  // user-quoted content.
  const head = content.trimStart().slice(0, 600);
  if (HEADER_PATTERNS.some(re => re.test(head))) return true;
  // Belt-and-braces: a Model: claude-… line paired with a Tokens: N/200k line
  // near the top is the unambiguous signature of Claude CLI's context report.
  if (MODEL_LINE.test(head) && TOKENS_LINE.test(head)) return true;
  return false;
};
